import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'

import { api } from '@/lib/api'
import { ApiHttpError } from '@/api/auth'
import { isDesktopShell } from '@/lib/desktopShell'

export interface Passkey {
  id: number
  name: string
  device_type: 'single_device' | 'multi_device'
  backed_up: boolean
  transports: string[]
  created_at: string
  last_used_at: string | null
}

export interface PasskeyLoginResponse {
  access: string
  refresh: string
}

/** Le serveur renvoie les options WebAuthn telles que générées par py_webauthn.
 * Le format est strictement celui attendu par @simplewebauthn/browser. */
type RegistrationOptionsResponse = PublicKeyCredentialCreationOptionsJSON

type AuthenticationOptionsResponse = PublicKeyCredentialRequestOptionsJSON & {
  auth_session_id: string
}

export function isPasskeySupported(): boolean {
  if (!browserSupportsWebAuthn()) return false
  // Dans le shell macOS (WKWebView), WebAuthn ne fonctionne QUE si l'app a
  // l'entitlement `webcredentials:app.callizy.ca` et que le serveur sert un
  // AASA compatible. La nouvelle version de Callizy.app injecte la flag
  // window.callizyMacSupportsPasskeys pour le signaler. Sans cette flag,
  // on masque les passkeys pour éviter un échec silencieux côté user.
  if (isDesktopShell() && !(window as Window & { callizyMacSupportsPasskeys?: boolean }).callizyMacSupportsPasskeys) {
    return false
  }
  return true
}

export function hasPlatformAuthenticator(): Promise<boolean> {
  return platformAuthenticatorIsAvailable()
}

// =============================================================================
// Enrôlement (user authentifié, depuis Settings)
// =============================================================================

/** Enrôle un nouveau passkey pour l'utilisateur courant.
 *
 * Effectue le round-trip complet :
 *   1. POST /register/begin/  → options + challenge
 *   2. startRegistration()    → prompt biométrique navigateur
 *   3. POST /register/complete/ → vérif + persistance
 */
export async function registerPasskey(name?: string): Promise<Passkey> {
  const { data: options } = await api.post<RegistrationOptionsResponse>(
    '/api/passkeys/register/begin/',
    { name: name ?? '' },
  )

  // startRegistration affiche le prompt système (Touch ID, Windows Hello,
  // YubiKey, etc.) et renvoie l'attestation signée par l'authenticator.
  const credential = await startRegistration({ optionsJSON: options })

  const { data: passkey } = await api.post<Passkey>(
    '/api/passkeys/register/complete/',
    { credential, name: name ?? '' },
  )
  return passkey
}

// =============================================================================
// Authentification (public, produit un JWT)
// =============================================================================

/** Se connecte via un passkey existant.
 *
 * Si `email` est fourni → on cible les credentials de cet utilisateur.
 * Sinon → flow username-less (le navigateur propose les passkeys
 * discoverable). C'est le bouton "Se connecter avec un passkey" sans
 * saisie préalable.
 *
 * Retourne les tokens JWT au même format que `/api/token/`.
 */
export async function loginWithPasskey(email?: string): Promise<PasskeyLoginResponse> {
  const body = email ? { email: email.trim().toLowerCase() } : {}
  const beginRes = await fetch(`${api.defaults.baseURL}/api/passkeys/authenticate/begin/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!beginRes.ok) {
    const data = await beginRes.json().catch(() => ({}))
    throw new ApiHttpError(beginRes.status, data)
  }
  const options = (await beginRes.json()) as AuthenticationOptionsResponse
  const { auth_session_id, ...optionsJSON } = options

  const assertion = await startAuthentication({ optionsJSON })

  const completeRes = await fetch(`${api.defaults.baseURL}/api/passkeys/authenticate/complete/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ auth_session_id, credential: assertion }),
  })
  if (!completeRes.ok) {
    const data = await completeRes.json().catch(() => ({}))
    throw new ApiHttpError(completeRes.status, data)
  }
  return completeRes.json()
}

// =============================================================================
// Gestion (settings)
// =============================================================================

export async function listPasskeys(): Promise<Passkey[]> {
  const { data } = await api.get<Passkey[]>('/api/passkeys/')
  return data
}

export async function deletePasskey(id: number): Promise<void> {
  await api.delete(`/api/passkeys/${id}/`)
}
